import browser from '@/lib/browser'
import device from '@/lib/device'
import { captureException, setUser } from '@sentry/vue'
import { getUserId } from '@/services/identity-client'

import mParticle from '@mparticle/web-sdk'
/**
 * Branch utility class
 */
class Branch {
  /**
   * Determines if the Branch script is loaded
   *
   * @returns Boolean
   */
  async isLoaded() {
    try {
      // attempt to hit the branch sdk location to see if it will get blocked
      await fetch('https://cdn.branch.io/branch-latest.min.js', {
        method: 'HEAD',
        mode: 'no-cors',
      })
      return true
    } catch (e) {
      captureException(e, {
        tags: {
          branch: 'unreachable',
        },
      })
      return false
    }
  }

  /**
   * Gets the key used to init Branch
   *
   * @returns String
   */
  getKey() {
    const { branchFishKey } = window.environment
    return branchFishKey
  }

  /**
   * Loads the branch script
   *
   * @returns Object
   */
  // prettier-ignore
  loadSnippet() {
    /* eslint-disable */
    (function(b,r,a,n,c,h,_,s,d,k){if(!b[n]||!b[n]._q){for(;s<_.length;)c(h,_[s++]);d=r.createElement(a);d.async=1;d.src="https://cdn.branch.io/branch-latest.min.js";k=r.getElementsByTagName(a)[0];k.parentNode.insertBefore(d,k);b[n]=h}})(window,document,"script","branch",function(b,r){b[r]=function(){b._q.push([r,arguments])}},{_q:[],_v:1},"addListener applyCode autoAppIndex banner closeBanner closeJourney creditHistory credits data deepview deepviewCta first getCode init link logout redeem referrals removeListener sendSMS setBranchViewData setIdentity track validateCode trackCommerceEvent logEvent disableTracking".split(" "), 0);
    return branch
    /* eslint-enable */
  }

  /**
   * @param {string} text - Text to copy to clipboard
   */
  copyToClipboard(text) {
    navigator.clipboard.writeText(text)
  }

  /**
   * Used to initialize branch on page load for journeys
   */
  initialize() {
    const branchSDK = this.loadSnippet()
    const key = this.getKey()
    branchSDK.init(key, (err) => {
      if (err) {
        captureException(err, {
          tags: {
            branch: 'page_load',
          },
        })
      }
    })
    branchSDK.setBranchViewData({
      data: {
        web_url: window.location.href,
      },
    })
  }

  /**
   * Generates referral link that user can copy and then share
   * @param {object} linkData
   * @param {boolean} copyLink - Copy link to clipboard or not
   */
  async generateReferralLink(linkData, copyLink = true, callback) {
    if (await this.isLoaded()) {
      const key = this.getKey()
      const branchSDK = this.loadSnippet()

      if (key && branchSDK) {
        const uuid = await getUserId()
        setUser({
          id: uuid,
        })
        branchSDK.init(key, function (err) {
          if (err) {
            captureException(err, {
              tags: {
                branch: 'init failed',
              },
            })
          }
        })

        branchSDK.setIdentity(uuid, function (err) {
          if (err) {
            captureException(err, {
              tags: {
                branch: 'set identity failed',
              },
            })
          }
        })

        branchSDK.link(linkData, function (err, link) {
          if (err) {
            captureException(err, {
              tags: {
                branch: 'genearate link failed',
              },
            })
            return
          }

          if (link) {
            if (copyLink) {
              this.copyToClipboard(link)
            }

            if (callback) {
              callback(link)
            }
          }
        })
      }
    }
  }

  /**
   * Create a new branch content share link and navigate to it. Is used to handle
   * opening a content share link on a mobile device when the app is not installed.
   *
   * @param {string} shareId - ID of share to open
   */
  // eslint-disable-next-line require-await
  async createAndOpenContentLink(shareId, queryLocation = undefined) {
    const key = this.getKey()
    const branchSDK = this.loadSnippet()
    this.clearPreviousSession()

    if (key && branchSDK) {
      branchSDK.init(key, (err) => {
        if (err) {
          captureException(err, {
            tags: {
              branch: 'init failed',
            },
            extra: {
              context: 'share_receive_page',
            },
          })
        }
      })

      device.checkDeviceID()

      const branchData = {
        share_id: shareId,
        landing_page_device_id: device.deviceID,
        destination: 'share',
      }

      // eslint-disable-next-line import/no-named-as-default-member
      const mParticleUser = mParticle.Identity.getCurrentUser()

      if (mParticleUser) {
        branchData['landing_page_mp_id'] = mParticleUser.getMPID()
        branchData['landing_page_mp_first_seen'] =
          mParticleUser.getFirstSeenTime()
      }

      if (queryLocation) {
        branchData['query_location'] = queryLocation
      }

      // get deferred deep link
      branchSDK.link(
        {
          data: branchData,
        },
        (err, link) => {
          if (err) {
            captureException(err, {
              tags: {
                branch: 'link_generation_failed',
              },
              extra: {
                context: 'share_receive_page',
              },
            })
          }

          browser.go(link)
        }
      )
    }
  }

  /**
   * Manually remove any stored branch session config to avoid cross-app
   * issues (Branch will return 500 internal server errors)
   */
  clearPreviousSession() {
    localStorage.removeItem('branch_session_first')
  }
}

const branchLib = new Branch()
export default branchLib
